// The phone's sideways swipes: across the page, right opens the sidebar and
// left the side panel; the side panel closes on a drag right (the sidebar is a
// drawer, which closes on its own). Touch events, not pointer events: iOS
// Safari only lets a page keep a gesture it claims on a touchmove.
import { useEffect, useRef, type RefObject } from "react";

/** Below `md`, where the sidebar is a drawer and the panel covers the screen. */
const PHONE = "(max-width: 767px)";
/** The strips at the screen's edges where Safari's Back and Forward swipes start. */
const EDGE = 20;

/**
 * On a phone, a touch that starts at the screen's left or right edge is the
 * page's, not Safari's: no Back or Forward swipe (Safari 13.4 and later honour
 * a canceled touchstart), so the swipes here can start there. A canceled
 * touchstart also cancels its tap, so a touch that ends where it began is
 * clicked by hand. A vertical scroll can't start in those strips.
 */
export function useEdgeSwipeGuard() {
  useEffect(() => {
    const phone = window.matchMedia(PHONE);
    let tap: { x: number; y: number; target: EventTarget | null } | null = null;
    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      tap = null;
      if (!phone.matches || event.touches.length !== 1 || !touch) return;
      if (touch.clientX > EDGE && touch.clientX < window.innerWidth - EDGE) return;
      event.preventDefault();
      tap = { x: touch.clientX, y: touch.clientY, target: event.target };
    };
    const onEnd = (event: TouchEvent) => {
      const touch = event.changedTouches[0];
      const was = tap;
      tap = null;
      if (!was || !touch) return;
      if (Math.abs(touch.clientX - was.x) > 10 || Math.abs(touch.clientY - was.y) > 10) return;
      const target = was.target instanceof HTMLElement ? was.target : null;
      if (target?.matches("input, textarea, select, [contenteditable='true']")) target.focus();
      target?.click();
    };
    document.addEventListener("touchstart", onStart, { passive: false });
    document.addEventListener("touchend", onEnd);
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
    };
  }, []);
}

/** Whether `target`, or anything between it and `root`, scrolls sideways (the strip, a code block). */
function scrollsSideways(target: Element, root: Element) {
  for (let el: Element | null = target; el && el !== root; el = el.parentElement) {
    const { overflowX } = getComputedStyle(el);
    if ((overflowX === "auto" || overflowX === "scroll") && el.scrollWidth > el.clientWidth) {
      return true;
    }
  }
  return false;
}

type Swipe = { x: number; y: number; at: number };

/**
 * Where a one-finger touch can start a sideways swipe: not in a field or
 * something that scrolls sideways. Null where it can't.
 */
function swipeStart(event: TouchEvent, root: Element): Swipe | null {
  const touch = event.touches[0];
  if (event.touches.length !== 1 || !touch) return null;
  const target = event.target as Element;
  if (target.closest("input, textarea, select, [contenteditable='true']")) return null;
  if (scrollsSideways(target, root)) return null;
  return { x: touch.clientX, y: touch.clientY, at: event.timeStamp };
}

/**
 * Whether a move from `start` is a swipe toward `sign` (1 right, -1 left):
 * past 10px, and more sideways than up or down. Null while it can't yet tell.
 */
function sideways(start: Swipe, touch: Touch, sign: 1 | -1): boolean | null {
  const dx = (touch.clientX - start.x) * sign;
  const dy = touch.clientY - start.y;
  if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return null;
  return dx > 0 && Math.abs(dy) <= dx;
}

/** `value`'s latest, for listeners added once. */
function useLatest<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

/**
 * On a phone, the panel follows a drag to the right and closes past a third of
 * its width (or on a flick); short of that it springs back. A drag that's
 * mostly up or down, or starts in a field or something that scrolls sideways,
 * is left to scroll. A canceled touch springs back. The close lands once the
 * slide ends, and only on the page the swipe started on: a navigation in
 * between (Back, another thread) drops it.
 */
export function useSwipeRightToClose(ref: RefObject<HTMLElement | null>, onClose?: () => void) {
  const close = useLatest(onClose);
  const enabled = Boolean(onClose);
  useEffect(() => {
    const panel = ref.current;
    if (!panel || !enabled) return;
    const phone = window.matchMedia(PHONE);
    let start: Swipe | null = null;
    let dragging = false;
    let dx = 0;
    let closing: number | undefined;
    const settle = () => {
      panel.style.transition = "";
      panel.style.transform = "";
    };
    const onStart = (event: TouchEvent) => {
      start = phone.matches ? swipeStart(event, panel) : null;
      dragging = false;
      dx = 0;
    };
    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!start || !touch) return;
      dx = touch.clientX - start.x;
      if (!dragging) {
        const right = sideways(start, touch, 1);
        if (right === null) return;
        if (!right) {
          start = null;
          return;
        }
        dragging = true;
        panel.style.transition = "none";
      }
      event.preventDefault();
      panel.style.transform = `translateX(${Math.max(0, dx)}px)`;
    };
    const onEnd = (event: TouchEvent) => {
      if (!start) return;
      const flick = dx / Math.max(1, event.timeStamp - start.at) > 0.5;
      const was = dragging;
      start = null;
      dragging = false;
      if (!was) return;
      panel.style.transition = "transform 200ms ease-out";
      if (dx > panel.clientWidth / 3 || (flick && dx > 40)) {
        panel.style.transform = "translateX(100%)";
        const from = window.location.href;
        closing = window.setTimeout(() => {
          closing = undefined;
          settle();
          if (window.location.href === from) close.current?.();
        }, 200);
      } else {
        panel.style.transform = "";
      }
    };
    const onCancel = () => {
      const was = dragging;
      start = null;
      dragging = false;
      if (!was) return;
      panel.style.transition = "transform 200ms ease-out";
      panel.style.transform = "";
    };
    panel.addEventListener("touchstart", onStart, { passive: true });
    panel.addEventListener("touchmove", onMove, { passive: false });
    panel.addEventListener("touchend", onEnd);
    panel.addEventListener("touchcancel", onCancel);
    return () => {
      panel.removeEventListener("touchstart", onStart);
      panel.removeEventListener("touchmove", onMove);
      panel.removeEventListener("touchend", onEnd);
      panel.removeEventListener("touchcancel", onCancel);
      window.clearTimeout(closing);
      settle();
    };
  }, [ref, enabled, close]);
}

/** Whether a drag from `start` toward `sign` has gone far enough to open: 60px, or a 30px flick. */
function farEnough(start: Swipe, touch: Touch, sign: 1 | -1, at: number) {
  const dx = (touch.clientX - start.x) * sign;
  return dx > 60 || (dx > 30 && dx / Math.max(1, at - start.at) > 0.5);
}

/**
 * On a phone, a drag across `ref` toward `side` (`right` or `left`) calls
 * `onOpen` once it's gone 60px (or flicked 30px). A drag that's mostly up or
 * down, or starts in a field or something that scrolls sideways, is left to
 * scroll. Off unless `enabled`.
 */
export function useSwipeToOpen(
  ref: RefObject<HTMLElement | null>,
  side: "left" | "right",
  onOpen: (() => void) | undefined,
  enabled: boolean,
) {
  const open = useLatest(onOpen);
  const on = enabled && Boolean(onOpen);
  const sign = side === "right" ? 1 : -1;
  useEffect(() => {
    const page = ref.current;
    if (!page || !on) return;
    const phone = window.matchMedia(PHONE);
    let start: Swipe | null = null;
    let dragging = false;
    const onStart = (event: TouchEvent) => {
      start = phone.matches ? swipeStart(event, page) : null;
      dragging = false;
    };
    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!start || !touch) return;
      const toward = dragging || sideways(start, touch, sign);
      if (toward === null) return;
      if (!toward) {
        start = null;
        return;
      }
      dragging = true;
      event.preventDefault();
      if (!farEnough(start, touch, sign, event.timeStamp)) return;
      start = null;
      open.current?.();
    };
    const onEnd = () => {
      start = null;
      dragging = false;
    };
    page.addEventListener("touchstart", onStart, { passive: true });
    page.addEventListener("touchmove", onMove, { passive: false });
    page.addEventListener("touchend", onEnd);
    page.addEventListener("touchcancel", onEnd);
    return () => {
      page.removeEventListener("touchstart", onStart);
      page.removeEventListener("touchmove", onMove);
      page.removeEventListener("touchend", onEnd);
      page.removeEventListener("touchcancel", onEnd);
    };
  }, [ref, on, open, sign]);
}
